import { useMemo } from 'react'

import { useMutation, useQuery } from '@apollo/react-hooks'
import { useFeature } from '@optimizely/react-sdk'

import {
  AddSecurityToPortfolioInput,
  AddSecurityToPortfolioResult,
  Etf,
  InvestmentAccount,
  InvestmentAccountType,
  RemoveSecurityFromPortfolioInput,
  RemoveSecurityFromPortfolioResult,
} from 'generated/types'

import addSecuritiesMutation from './mutations/addSecurityToPortfolio.gql'
import removeSecuritiesMutation from './mutations/removeSecurityFromPortfolio.gql'
import cryptoEtfQuery from './queries/cryptoEtf.gql'

interface CryptoEtfQueryResponse {
  cryptoEtf?: Etf
  investmentAccountsByUserId: InvestmentAccount[]
}

interface AddCryptoToPortfolioGqlResponse {
  addSecurityToPortfolio: AddSecurityToPortfolioResult
}

interface RemoveCryptoFromPortfolioGqlResponse {
  removeSecurityFromPortfolio: RemoveSecurityFromPortfolioResult
}

interface AddCryptoToPortfolioInput {
  input: AddSecurityToPortfolioInput
}

interface RemoveCryptoFromPortfolioInput {
  input: RemoveSecurityFromPortfolioInput
}

export const CRYPTO_GRADIENT = ['#6944FF', '#031125']
export const CRYPTO_SYMBOL = 'BITO'

export const useCrypto = () => {
  const [isCryptoEnabled, clientReady] = useFeature('ip_new_etf_web')

  const { data: cryptoData, loading: isCryptoDataLoading } = useQuery<CryptoEtfQueryResponse>(
    cryptoEtfQuery,
  )

  const [
    addCryptoToPortfolio,
    { data: addCryptoToPortfolioData, loading: isAddCryptoMutationSubmitting },
  ] = useMutation<AddCryptoToPortfolioGqlResponse, AddCryptoToPortfolioInput>(addSecuritiesMutation)
  const [
    removeCryptoFromPortfolio,
    { data: removeCryptoFromPortfolioData, loading: isRemoveCryptoMutationSubmitting },
  ] = useMutation<RemoveCryptoFromPortfolioGqlResponse, RemoveCryptoFromPortfolioInput>(
    removeSecuritiesMutation,
  )

  const cryptoEtf = cryptoData?.cryptoEtf?.symbol === CRYPTO_SYMBOL ? cryptoData.cryptoEtf : null
  const { investmentAccount, currentPortfolio } = useMemo(() => {
    const investmentAccountInfo = cryptoData?.investmentAccountsByUserId.find(
      account => account.investmentAccountType === InvestmentAccountType.BasicInvestment,
    )
    let currentPortfolioInfo = investmentAccountInfo?.portfolio

    if (addCryptoToPortfolioData?.addSecurityToPortfolio.__typename === 'Portfolio') {
      currentPortfolioInfo = addCryptoToPortfolioData.addSecurityToPortfolio
    }

    if (removeCryptoFromPortfolioData?.removeSecurityFromPortfolio.__typename === 'Portfolio') {
      currentPortfolioInfo = removeCryptoFromPortfolioData.removeSecurityFromPortfolio
    }

    return {
      investmentAccount: investmentAccountInfo,
      currentPortfolio: currentPortfolioInfo,
    }
  }, [cryptoData, addCryptoToPortfolioData, removeCryptoFromPortfolioData])

  const { eligibleForCrypto, currentPortfolioIncludesCrypto } = useMemo(() => {
    if (clientReady && !isCryptoEnabled) {
      return {
        eligibleForCrypto: false,
        currentPortfolioIncludesCrypto: false,
      }
    }

    const currentPortfolioIncludesCryptoInfo =
      investmentAccount?.portfolio?.etfs?.some(etf => etf.investmentCategories.crypto > 0) ?? false
    return {
      eligibleForCrypto: !!(cryptoEtf && !currentPortfolioIncludesCryptoInfo),
      currentPortfolioIncludesCryptoInfo,
    }
  }, [cryptoData, investmentAccount])

  // @note: If the current portfolio includes crypto but we still
  // haven't purchased a share for the user, their portfolio
  // is still in an "unbalanced" state. This logic drives a
  // card where we explain this to the user.
  const isPortfolioBeingRebalanced =
    currentPortfolioIncludesCrypto && !!((investmentAccount?.totalCrypto?.value ?? 0) <= 0)

  return {
    isCryptoEnabled,
    cryptoEtf,
    isCryptoDataLoading,
    investmentAccount,
    eligibleForCrypto,
    currentPortfolioIncludesCrypto,
    addCryptoToPortfolio,
    removeCryptoFromPortfolio,
    currentPortfolio,
    isAddCryptoMutationSubmitting,
    isRemoveCryptoMutationSubmitting,
    cryptoEtfSymbol: cryptoEtf?.symbol ?? CRYPTO_SYMBOL,
    isPortfolioBeingRebalanced,
  }
}
